/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ValidationErrors } from '@angular/forms';
import * as strings from '@touchplan/strings';

export class NumberUtils {
	/**
	 * Rounds the given number to two decimal places
	 * @param num The number to round
	 * @returns The rounded result
	 */
	public static roundToTwo(num: number) {
		// Why "+/- Number.EPSILON" you ask?
		// Because it's needed for "roundToTwo(1.005)" to produce the correct result of 1.01
		// that's why, Smarty Pants.
		const delta = num < 0 ? -Number.EPSILON : Number.EPSILON;
		return Math.round((num + delta) * 100) / 100;
	}

	/**
	 * Converts the given string to a number if possible.  If the conversion isn't possible,
	 * a ValidationErrors is returned; otherwise the number is returned.
	 * 'E' or 'e' notation (such as '1e5') is allowed depending on "allowExponents".
	 * Floating point is allowed depending on "allowFloatingPoint".
	 * A label name for the string being converted must be provided as "dataLabel".
	 *
	 * @param stringToConvert - the string to convert to a number
	 * @param allowFloatingPoint - boolean indicating whether floating point is allowed
	 * @param  allowExponents - boolean indicating whether exponents allowed
	 * @param  dataLabel - name of the string to convert, for error messages
	 * @return converted number or ValidationErrors
	 */
	public static stringToNumberWithValidation(
		stringToConvert: string,
		allowFloatingPoint: boolean,
		allowExponents: boolean,
		dataLabel: string
	): ValidationErrors | number {
		if (stringToConvert === null || stringToConvert === undefined) {
			return NumberUtils.numberFormatError(strings.NUMBER_SPINNER_VALUE_IS_MISSING(dataLabel));
		}
		const sTrim: string = stringToConvert.trim();
		if (sTrim.length === 0) {
			return NumberUtils.numberFormatError(strings.NUMBER_SPINNER_VALUE_IS_MISSING(dataLabel));
		}
		// Check for illegal characters:
		const illegalChars = [];
		if (!allowFloatingPoint) {
			illegalChars.push('.');
		}
		if (!allowExponents) {
			illegalChars.push('e');
			illegalChars.push('E');
		}
		for (const c of illegalChars) {
			if (stringToConvert.includes(c)) {
				return NumberUtils.numberFormatError(strings.NUMBER_SPINNER_VALUE_IS_INVALID(dataLabel));
			}
		}
		// Double-check the floating point thing:
		const n = Number(stringToConvert);
		if (allowFloatingPoint) {
			if (Number.isFinite(n)) {
				// Excludes NaN (most importantly), and positive and negative Infinity:
				return n;
			}
			return NumberUtils.numberFormatError(strings.NUMBER_SPINNER_VALUE_IS_INVALID(dataLabel));
		}
		// So here we know:  floating point is NOT allowed:
		if (!Number.isInteger(n)) {
			return NumberUtils.numberFormatError(strings.NUMBER_SPINNER_VALUE_IS_INVALID(dataLabel));
		}
		return n;
	}

	private static numberFormatError(s: string): ValidationErrors {
		return { numberFormatError: s } as ValidationErrors;
	}

	/**
	 * Decrements the given "value" by the given "step".  However, the
	 * return result will not be allowed to go below "min".  Also, if
	 * "value" is not a whole number, it's rounded down to the nearest
	 * integer.  "step" must be a whole number.
	 *
	 * @param value - the value to decrement
	 * @param min - the minimum value that can be returned
	 * @param step - the amount to decrement by; must be a whole number
	 * @return decremented number
	 */
	public static decrement(value: number, min: number, step: number): number {
		if (value === null || value === undefined) {
			return min;
		}
		let decremented = Math.floor(value); // Required for non-whole number, floating point values
		if (decremented === value) {
			// It was whole to begin with:
			decremented = value - step;
		}
		return Math.max(decremented, min);
	}

	/**
	 * Increments the given "value" by the given "step".  However, the
	 * return result will not be allowed to go above "max".  Also, if
	 * "value" is not a whole number, it's rounded up to the nearest
	 * integer.  "step" must be a whole number.
	 *
	 * @param value - the value to increment
	 * @param max - the maximum value that can be returned
	 * @param step - the amount to increment by; must be a whole number
	 * @return incremented number
	 */
	public static increment(value: number, max: number, step: number): number {
		if (value === null || value === undefined) {
			return max;
		}
		let decremented = Math.ceil(value); // Required for non-whole number, floating point values
		if (decremented === value) {
			// It was whole to begin with:
			decremented = value + step;
		}
		return Math.min(decremented, max);
	}
}
